import {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
  Fragment,
  forwardRef,
} from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { Collapse, IconButton, Skeleton } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

// Virtualization
import AutoSizer from "react-virtualized-auto-sizer";
import { List } from "react-window";

// react-router
import { useSearchParams, Link as RouterLink } from "react-router-dom";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  canSort,
  manualSortFunction,
  isScrollable,
  noEndBorder,
  showEntriesPerPage,
  setCsvData,
  loading,
  includeDetails,
  defaultExpanded,
  rowProps,
  rowOptions,
  sx,
  // NEW
  virtualize = false,
  rowHeight = 52, // default MUI dense row height
  detailHeight = 200, // fallback height for expanded content (overridden dynamically)
  overscan = 6,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState({});
  const defaultValue = entriesPerPage.defaultValue
    ? entriesPerPage.defaultValue
    : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
      manualSortBy: Boolean(manualSortFunction),
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0
      ? gotoPage(0)
      : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) =>
    gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle (FIX: update local state correctly)
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  function exportToCsv() {
    return [
      table.columns
        .map((column) => column.Header)
        .filter((item) => item !== ""),
      ...table.rows.map((row) => {
        return table.columns
          .map((column) => {
            return row[column.accessor + "Csv"];
          })
          .filter((item) => item);
      }),
    ];
  }

  const exportToCsvMemoized = useMemo(exportToCsv, [table]);

  const prevTable = useRef();

  useEffect(() => {
    if (
      setCsvData &&
      (!prevTable.current ||
        JSON.stringify(prevTable.current) != JSON.stringify(table))
    ) {
      prevTable.current = table;
      if (setCsvData) setCsvData(exportToCsvMemoized);
    }
  }, [table, setCsvData]);

  // --- Virtualization helpers -------------------------------------------------
  // Flatten page rows to include optional detail rows so we can virtualize a single list
  const flatItems = useMemo(() => {
    if (!virtualize) return [];
    const items = [];
    page.forEach((row) => {
      items.push({ type: "row", row });
      const reactKey = row.original?.data?.id;
      const isOpen =
        includeDetails &&
        (defaultExpanded ? !expanded[reactKey] : expanded[reactKey]);
      if (isOpen) items.push({ type: "detail", row });
    });
    return items;
  }, [page, expanded, includeDetails, defaultExpanded, virtualize]);

  const listRef = useRef(null);
  const sizeMapRef = useRef({});

  const getItemSize = useCallback(
    (index) => {
      const cached = sizeMapRef.current[index];
      if (typeof cached === "number") return cached;
      const fallback =
        flatItems[index]?.type === "detail" ? detailHeight : rowHeight;
      return fallback;
    },
    [flatItems, rowHeight, detailHeight],
  );

  const setSize = useCallback((index, size) => {
    if (sizeMapRef.current[index] !== size) {
      sizeMapRef.current[index] = size;
      if (listRef.current) {
        // Recompute from this index onward
        listRef.current.resetAfterIndex(index);
      }
    }
  }, []);

  // Recompute sizes when expansion toggles
  useEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0, true);
  }, [expanded]);

  const OuterElement = forwardRef(function OuterElement(props, ref) {
    // Make the react-window outer element behave like a TableBody so the rest of the table styles apply
    return <TableBody ref={ref} {...getTableBodyProps()} {...props} />;
  });

  // Row renderer for virtualized list
  const VirtualRow = ({ index, style }) => {
    const item = flatItems[index];
    if (!item) return null;

    const { row } = item;
    const reactKey = row.original?.data?.id;

    prepareRow(row);
    if (row.original?.hidden) return null;

    return (
      <div
        style={style}
        ref={(el) => {
          if (el) {
            const measured = el.getBoundingClientRect().height;
            setSize(index, measured);
          }
        }}
      >
        {item.type === "row" ? (
          <TableRow
            {...rowProps}
            {...row.getRowProps()}
            key={"KEY:" + (reactKey || index)}
          >
            {row.cells.map((cell, cellIndex) => (
              <DataTableBodyCell
                noBorder={noEndBorder && rows.length - 1 === index}
                key={(reactKey ? `${reactKey}-` : "cell-") + cellIndex}
                align={cell.column.align ? cell.column.align : "left"}
                width={cell?.column.width || "100%"}
                {...cell.getCellProps()}
                sx={{
                  ...(cell.getCellProps().sx || {}),
                  ...(cell?.column?.bodyCellStyle || {}),
                }}
                isLastCell={isScrollable && cellIndex === row.cells.length - 1}
                hidden={row.original?.hidden}
                onClick={
                  includeDetails
                    ? () => {
                        setExpanded((prev) => ({
                          ...prev,
                          [reactKey]: !prev[reactKey],
                        }));
                      }
                    : null
                }
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {cellIndex === 0 && includeDetails ? (
                    <IconButton size="small">
                      <ArrowDropDown
                        sx={{
                          transform: (
                            defaultExpanded
                              ? !expanded[reactKey]
                              : expanded[reactKey]
                          )
                            ? undefined
                            : "rotate(-90deg)",
                          transition: "all 0.2s ease-in-out",
                        }}
                      />
                    </IconButton>
                  ) : null}
                  {cell.render("Cell")}
                </Box>
              </DataTableBodyCell>
            ))}
          </TableRow>
        ) : (
          // detail row
          <TableRow
            {...row.getRowProps()}
            key={"DETAIL:" + (reactKey || index)}
          >
            <Box
              component="td"
              colSpan={columns.length}
              sx={(theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.palette.light.main}`,
                pb: 1.5,
                px: 3,
              })}
            >
              {includeDetails(row)}
            </Box>
          </TableRow>
        )}
      </div>
    );
  };

  // Setting the entries starting point
  const entriesStart =
    pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;
  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none", height: "100%", ...sx }}>
      {showEntriesPerPage && (
        <>
          {entriesPerPage || canSearch ? (
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={3}
            >
              {entriesPerPage && (
                <MDBox display="flex" alignItems="center">
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setEntriesPerPage(parseInt(newValue, 10));
                    }}
                    size="small"
                    sx={{ width: "5rem" }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                  <MDTypography variant="caption" color="secondary">
                    &nbsp;&nbsp;entries per page
                  </MDTypography>
                </MDBox>
              )}
              {canSearch && (
                <MDBox width="12rem" ml="auto">
                  <MDInput
                    placeholder="Search..."
                    value={search || ""}
                    size="small"
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                      onSearchChange(currentTarget.value);
                    }}
                  />
                </MDBox>
              )}
            </MDBox>
          ) : null}
        </>
      )}

      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => {
                const props = column.getHeaderProps(
                  isSorted &&
                    canSort?.includes(column["Header"]) &&
                    column.getSortByToggleProps(),
                );

                const onClick = props.onClick;
                const sortable = Boolean(canSort?.includes(column["Header"]));

                if (sortable && manualSortFunction) {
                  props.onClick = (...params) => {
                    onClick?.(...params);
                    let direction = "asce";
                    if (
                      searchParams.get("sortBy")?.split("-")[0] == column.id
                    ) {
                      const paramsDirection = searchParams
                        .get("sortBy")
                        ?.split("-")[1];
                      direction =
                        paramsDirection == "asce"
                          ? "desc"
                          : paramsDirection == "desc"
                            ? "none"
                            : "asce";
                    }

                    setSearchParams((curr) => {
                      curr.set("sortBy", `${column.id}-${direction}`);
                      return curr;
                    });

                    manualSortFunction(column.id, direction);
                  };
                }

                let sorted = canSort?.includes(column["Header"])
                  ? setSortedValue(column)
                  : false;

                if (sortable && manualSortFunction) {
                  sorted =
                    searchParams.get("sortBy")?.split("-")[0] == column.id
                      ? searchParams.get("sortBy").split("-")[1]
                      : "none";
                }

                if (!props.onClick) props.onClick = () => {};

                return (
                  <DataTableHeadCell
                    {...props}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sx={column?.headCellStyle || {}}
                    sorted={sorted}
                    isLastCell={
                      isScrollable && index === headerGroup.headers.length - 1
                    }
                    sortable={Boolean(canSort?.includes(column["Header"]))}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                );
              })}
            </TableRow>
          ))}
        </MDBox>

        {/* BODY */}
        {!loading && !virtualize && (
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              const reactKey = row.original.data?.id;
              prepareRow(row);
              if (row.original.hidden) return null;
              return (
                <Fragment key={"outer KEY: " + (reactKey || key)}>
                  <TableRow
                    {...rowProps}
                    {...row.getRowProps()}
                    key={"KEY: " + (reactKey || key)}
                    sx={
                      rowOptions?.to(row.original?.data)
                        ? {
                            ...rowProps?.sx,
                            ...row?.getRowProps?.()?.sx,
                            "&:hover": {
                              backgroundColor: "whitesmoke !important", // Added '&' and '!important'
                              cursor: "pointer", // Optional: makes it obvious it's interactive since it has a link
                            },
                          }
                        : {
                            ...rowProps?.sx,
                            ...row?.getRowProps?.()?.sx,
                          }
                    }
                    component={
                      rowOptions?.to(row.original?.data)
                        ? RouterLink
                        : undefined
                    }
                    to={rowOptions?.to(row.original?.data)}
                  >
                    {(row.cells || []).map((cell, index) => (
                      <DataTableBodyCell
                        noBorder={noEndBorder && rows.length - 1 === key}
                        key={reactKey + " - " + index || "Key of cell: " + key}
                        align={cell.column.align ? cell.column.align : "left"}
                        width={cell?.column.width || "100%"}
                        {...cell.getCellProps()}
                        sx={{
                          ...(cell.getCellProps().sx || {}),
                          ...(cell?.column?.bodyCellStyle || {}),
                        }}
                        isLastCell={
                          isScrollable && index === row.cells.length - 1
                        }
                        hidden={row.original.hidden}
                        onClick={
                          includeDetails
                            ? () => {
                                setExpanded((prev) => ({
                                  ...prev,
                                  [reactKey]: !prev[reactKey],
                                }));
                              }
                            : null
                        }
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {index == 0 && includeDetails ? (
                            <IconButton size="small">
                              <ArrowDropDown
                                sx={{
                                  transform: (
                                    defaultExpanded
                                      ? !expanded[reactKey]
                                      : expanded[reactKey]
                                  )
                                    ? undefined
                                    : "rotate(-90deg)",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              />
                            </IconButton>
                          ) : null}
                          {cell.render("Cell")}
                        </Box>
                      </DataTableBodyCell>
                    ))}
                  </TableRow>
                  {includeDetails &&
                  (defaultExpanded
                    ? !expanded[reactKey]
                    : expanded[reactKey]) ? (
                    <TableRow
                      {...row.getRowProps()}
                      key={"2KEY: " + (reactKey || key)}
                    >
                      <Box
                        component="td"
                        colSpan={columns.length}
                        sx={(theme) => ({
                          borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.palette.light.main}`,
                          pb: 1.5,
                          px: 3,
                        })}
                      >
                        {includeDetails(row)}
                      </Box>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        )}

        {!loading && virtualize && (
          <AutoSizer disableWidth>
            {({ height }) => (
              <List
                height={height || 400}
                width="100%"
                itemCount={flatItems.length}
                itemSize={getItemSize}
                outerElementType={OuterElement}
                overscanCount={overscan}
                ref={listRef}
              >
                {VirtualRow}
              </List>
            )}
          </AutoSizer>
        )}
      </Table>

      {loading && (
        <Box
          sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row, key) => (
            <Skeleton
              key={key}
              sx={{
                width: "100%",
                height: `${rowHeight}px`,
                padding: 0,
                margin: 0,
                borderRadius: "8px",
                mt: -1.25,
                mb: -1.25,
              }}
            />
          ))}
        </Box>
      )}

      {!loading && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 3, sm: 0 }}>
              <MDTypography
                variant="button"
                color="secondary"
                fontWeight="regular"
              >
                Showing {entriesStart} to {entriesEnd} of {rows.length} entries
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => previousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{
                      type: "number",
                      min: 1,
                      max: customizedPageOptions.length,
                    }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={
                      (handleInputPagination, handleInputPaginationValue)
                    }
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={() => nextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 50000, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  showEntriesPerPage: false,
  virtualize: false,
  rowHeight: 52,
  detailHeight: 200,
  overscan: 6,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  showEntriesPerPage: PropTypes.bool,
  // NEW
  virtualize: PropTypes.bool,
  rowHeight: PropTypes.number,
  detailHeight: PropTypes.number,
  overscan: PropTypes.number,
};

export default DataTable;
